import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import { translations } from '../translations/translations'
import { RussianFlag, BritishFlag } from './Flags'
import { useAuth } from '../contexts/AuthContext'
import alricheLogo from '../img/alriche.png'
import aaLogo from '../img/logo.jpg'

const Header = () => {
  const { language, changeLanguage } = useLanguage()
  const { getCartCount } = useCart()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [showLeftMenu, setShowLeftMenu] = useState(false)
  const [isOverlay, setIsOverlay] = useState(false)
  const headerRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [activeSection, setActiveSection] = useState(null)
  const t = translations.header
  const footerT = translations.footer
  const leftT = translations.headerLeftMenu


  useEffect(() => {
    if (showMobileMenu || showLeftMenu) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [showMobileMenu, showLeftMenu])

  useEffect(() => {
    const onScroll = () => {
      const header = headerRef.current;
      if (!header) return;
      
      const scrolled = window.pageYOffset > 50;
      setIsOverlay(window.pageYOffset > 10);
      
      if (scrolled) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [])

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight || 0)
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  useEffect(() => {
    const handler = () => { setShowMobileMenu(false); setShowLeftMenu(false) }
    window.addEventListener('auth:signedin', handler)
    return () => window.removeEventListener('auth:signedin', handler)
  }, [])

  const languages = [
    { code: 'ru', name: 'RU', fullName: 'Русский', flagComponent: RussianFlag },
    { code: 'en', name: 'EN', fullName: 'English', flagComponent: BritishFlag }
  ]

  const currentLang = languages.find(lang => lang.code === language)

  const location = useLocation()
  const hideSpacer =
    location.pathname === '/cart' ||
    location.pathname === '/checkout' ||
    location.pathname.startsWith('/shop') ||
    (location.pathname === '/' && location.state?.fromOrderSuccess)

  const menuItems = [
    // What Angelina Alriche is doing now (store renovation)
    {
      href: '/#tienda',
      label: language === 'ru' ? 'Что делает сейчас ANGELINA\u00A0\u00A0ALRICHE' : 'ANGELINA\u00A0\u00A0ALRICHE is doing now',
      isAnchor: true
    },
    // Jewellery Collection
    {
      href: '/#jewellery-collection',
      label: language === 'ru' ? 'Ювелирная коллекция' : 'Jewellery Collection',
      isAnchor: true
    },
    // Luxury Fabrics
    {
      href: '/#luxury-fabrics',
      label: language === 'ru' ? 'Роскошные ткани' : 'Luxury Fabrics',
      isAnchor: true
    },
    // For Little Princess Collection
    { 
      href: '/shop?category=Kids', 
      label: language === 'ru' ? 'Коллекция для маленькой принцессы' : 'For Little Princess Collection', 
      isAnchor: false 
    },
    // Our sewing masters
    { 
      href: '/#clothing-production', 
      label: translations.clothingProduction.title[language], 
      isAnchor: true 
    },
    // SHOP
    { 
      href: '/shop', 
      label: t.shop[language], 
      isAnchor: false 
    },
    // History
    { 
      href: '/#about', 
      label: translations.story.title[language], 
      isAnchor: true 
    }
  ]

  const rightMenuLabels = new Set(menuItems.map((item) => item.label))

  const sectionMenuItems = [
    {
      href: '/#world',
      label: translations.philosophy.title[language]
    },
    {
      href: '/#lady-in-red',
      label: translations.ladyInRed.headingTitle[language]
    },
    {
      href: '/#white-couture',
      label: language === 'ru' ? 'Белая коллекция' : 'White Collection'
    },
    {
      href: '/#purple-couture',
      label: language === 'ru' ? 'Фиолетовая коллекция' : 'Purple Collection'
    },
    {
      href: '/#jewellery-couture',
      label: translations.jewelleryFashion.title[language]
    },
    {
      href: '/#cocktail',
      label: language === 'ru' ? 'Коктейльные платья' : 'Cocktail Dresses'
    },
    {
      href: '/#about',
      label: translations.story.title[language]
    },
    {
      href: '/shop?category=kids',
      label: language === 'ru' ? 'Коллекция для маленькой принцессы' : 'Collection for Little Princess'
    }
  ]

  const leftMenuItems = [
    // World Of Fashion
    {
      href: '/#world',
      label: translations.philosophy.title[language],
      isAnchor: true
    },
    // Suits For Lady
    {
      href: '/#white-collection-title',
      label: language === 'ru' ? 'Костюмы для леди' : 'Suits For Lady',
      isAnchor: true
    },
    // White Suits
    {
      href: '/#white-couture',
      label: language === 'ru' ? 'Белые Suits' : 'White Suits',
      isAnchor: true
    },
    // Lilac Suits
    {
      href: '/#purple-couture',
      label: language === 'ru' ? 'Сиреневые Suits' : 'Lilac Suits',
      isAnchor: true
    },
    // Black Suits
    {
      href: '/#black-Suits',
      label: language === 'ru' ? 'Черные Suits' : 'Black Suits',
      isAnchor: true
    },
    // Red collection
    {
      href: '/#red-collection',
      label: translations.featuredCollections.redCollectionTitle[language],
      isAnchor: true
    },
    // Luxury Collection Handmaker For Sale
    {
      href: '/#luxury-handmaker',
      label: translations.featuredCollections.luxuryHandmakerTitle[language],
      isAnchor: true
    },
    // Cocktail Dresses
    {
      href: '/#cocktail',
      label: language === 'ru' ? 'Коктейльные платья' : 'Cocktail Dresses',
      isAnchor: true
    },
    // Red collection (duplicate removed - keeping first instance)
    // Black and White Collection
    {
      href: '/#black-and-white-collection',
      label: translations.featuredCollections.blackAndWhiteTitle[language],
      isAnchor: true
    },
    // Collection for order
    {
      href: '/#collection-for-sale',
      label: translations.featuredCollections.collectionForSaleTitle[language],
      isAnchor: true
    },
    // Wedding Dresses
    {
      href: '/#wedding-dresses',
      label: language === 'ru' ? 'Свадебные платья' : 'Wedding Dresses',
      isAnchor: true
    }
  ]

  const cartCount = getCartCount()

  useEffect(() => {
    const handler = (event) => {
      if (!event || !event.detail) return
      const id = event.detail.id || null

      // Если мы уже заметно проскроллили вниз, не даём hero перебивать
      // активную секцию (особенно вокруг White/Purple Suits).
      if (id === 'hero' && typeof window !== 'undefined' && window.pageYOffset > 150) {
        return
      }

      setActiveSection(id)
    }

    window.addEventListener('section:change', handler)
    return () => window.removeEventListener('section:change', handler)
  }, [])

  // Ensure correct logo state when changing routes
  useEffect(() => {
    if (location.pathname !== '/') {
      // On non-home pages always show the main ALRICHE logo
      setActiveSection(null)
      return
    }

    // On the home page, if we are at the very top, treat it as the hero section
    if (typeof window !== 'undefined' && window.pageYOffset < 50) {
      setActiveSection('hero')
    }
  }, [location.pathname])

  // При скролле наверх (почти к самому верху) всегда возвращаем hero,
  // чтобы логотип ALRICHE снова появлялся, даже если observer не сработал.
  useEffect(() => {
    if (location.pathname !== '/') return

    const handleScrollTopHero = () => {
      if (typeof window === 'undefined') return
      if (window.pageYOffset < 60 && activeSection !== 'hero') {
        setActiveSection('hero')
      }
    }

    window.addEventListener('scroll', handleScrollTopHero, { passive: true })
    handleScrollTopHero()

    return () => window.removeEventListener('scroll', handleScrollTopHero)
  }, [location.pathname, activeSection])

  const getLogoTitle = () => {
    if (!activeSection) return 'ALRICHE'

    switch (activeSection) {
      case 'world':
        return translations.philosophy.title[language]
      case 'lady-in-red':
        return translations.ladyInRed.headingTitle[language]
      case 'wedding-dresses':
        return language === 'ru' ? 'Свадебные платья' : 'Wedding Dresses'
      case 'clothing-production':
        return translations.clothingProduction.title[language]
      case 'luxury-fabrics':
        return language === 'ru' ? 'Роскошные ткани' : 'Luxury Fabrics'
      case 'red-collection':
        return translations.featuredCollections.redCollectionTitle[language]
      case 'luxury-handmaker':
        return translations.featuredCollections.luxuryHandmakerTitle[language]
      case 'black-and-white-collection':
        return translations.featuredCollections.blackAndWhiteTitle[language]
      case 'collection-for-sale':
        return translations.featuredCollections.collectionForSaleTitle[language]
      case 'white-couture':
        return language === 'ru' ? 'Белые костюмы' : 'White Suits'
      case 'purple-couture':
        return language === 'ru' ? 'Сиреневые костюмы' : 'Lilac Suits'
      case 'black-Suits':
        return language === 'ru' ? 'Черные костюмы' : 'Black Suits'
      case 'white-collection-gallery':
        return language === 'ru' ? 'Белые костюмы' : 'White Suits'
      case 'jewellery-collection':
        return language === 'ru' ? 'Ювелирная коллекция' : 'JEWELLERY COLLECTION'
      case 'jewellery-couture':
        return translations.jewelleryFashion.title[language]
      case 'project-jewellery-shop':
        return language === 'ru' ? 'Проект ювелирный бутик' : 'Project Jewellery Shop'
      case 'cocktail':
        return language === 'ru' ? 'Коктейльные платья' : 'Cocktail Dresses'
      case 'about':
        return translations.story.title[language]
      case 'world-end':
        return 'ALRICHE'
      case 'hero':
      default:
        return 'ALRICHE'
    }
  }

  const handleAnchorClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault()
      const id = href.substring(2) // Remove '/#'
      
      // If we're on another page, navigate to home first
      if (window.location.pathname !== '/') {
        window.location.href = href
      } else {
        // Smooth scroll to element
        let targetId = id
        // For White Collection, scroll specifically to the banner title span
        if (id === 'white-couture') {
          targetId = 'white-collection-title'
        }

        const element = document.getElementById(targetId)
        if (element) {
          let headerOffset = window.innerWidth < 640 ? 120 : 80

          if (id === 'white-couture') {
            headerOffset = window.innerWidth < 640 ? 180 : 140
          }
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }
      // На мобильных иногда observer срабатывает с задержкой,
      // поэтому дополнительно вручную уведомляем о смене секции.
      window.dispatchEvent(
        new CustomEvent('section:change', {
          detail: { id }
        })
      )
      setShowMobileMenu(false)
      setShowLeftMenu(false)
    }
  }

  return (
    <>
      <header ref={headerRef} className={`header ${isOverlay ? 'header--scrolled' : ''}`} id="header">
        <div className="header-container">
          <div className="header-left">
            <div
              className="menu-toggle"
              id="menuToggle"
              onClick={() => { setShowLeftMenu(!showLeftMenu); setShowMobileMenu(false); }}
              role="button"
              aria-label={showLeftMenu ? t.menuToggleClose[language] : t.menuToggleOpen[language]}
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <Link
            to="/"
            className="logo-text block select-none"
            aria-label={t.logoHomeLabel[language]}
            onClick={() => { setShowMobileMenu(false); setShowLeftMenu(false); }}
          >
            <div className="flex items-center justify-center gap-3">
              <img
                src={alricheLogo}
                alt="ALRICHE"
                className="site-logo w-auto object-contain select-none"
              />
            </div>
          </Link>

          <div className="header-right">
            <div
              className="menu-toggle"
              id="rightMenuToggle"
              onClick={() => { setShowMobileMenu(!showMobileMenu); setShowLeftMenu(false); }}
              role="button"
              aria-label={showMobileMenu ? t.menuToggleClose[language] : t.menuToggleOpen[language]}
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </header>

      {/* Left Mobile Menu */}
      <div 
        className={`fixed inset-0 z-40 lg:z-[60] transition-opacity duration-300 ${
          showLeftMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            showLeftMenu ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setShowLeftMenu(false)}
        ></div>
        {/* Menu Content */}
        <div 
          className={`absolute top-0 left-0 h-full w-full sm:w-80 bg-white shadow-2xl transform transition-transform duration-300 ${
            showLeftMenu ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            <div className={`flex-1 overflow-y-auto px-6 pt-40 pb-8 ${isOverlay ? 'lg:pt-28' : 'lg:pt-24'}`}>
              <nav className="space-y-6">
                {leftMenuItems.map((item, index) => (
                  item.isAnchor ? (
                    <a
                      key={index}
                      href={item.href}
                      onClick={(e) => handleAnchorClick(e, item.href)}
                      className="block text-lg font-medium tracking-wide text-gray-800 hover:text-black transition-colors duration-300 montserrat"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      to={item.href}
                      className="block text-lg font-medium tracking-wide text-gray-800 hover:text-black transition-colors duration-300 montserrat"
                      onClick={() => setShowLeftMenu(false)}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
              </nav>

              {/* Contacts (Left Menu) */}
              <div className="mt-12 pt-8 border-t border-gray-100">
                <h4 className="text-lg font-semibold mb-6 playfair">{footerT.contacts[language]}</h4>

                {/* Contact Info (moved WhatsApp to bottom below email) */}

                {/* Social Media */}
                <div className="flex flex-wrap gap-2">
                  {/* Facebook */}
                  <a
                    href="https://www.facebook.com/angelina.alriche.2025"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowLeftMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-facebook-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* Instagram main */}
                  <a
                    href="https://instagram.com/angelinaalriche"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowLeftMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-600 hover:to-orange-400 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-instagram-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* YouTube */}
                  <a
                    href="https://youtube.com/@Richshel"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowLeftMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-red-600 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-youtube-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* Telegram */}
                  <a
                    href="http://t.me/ALRICHE"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowLeftMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-sky-500 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-telegram-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/34662975699"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowLeftMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-green-500 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-whatsapp-line text-xs text-green-600 group-hover:text-white transition-colors"></i>
                  </a>
                </div>

                {/* Email at the very bottom */}
                <div className="mt-6">
                  <a 
                    href="mailto:angelinaalriche@gmail.com"
                    onClick={() => setShowLeftMenu(false)}
                    className="flex items-center text-gray-700 hover:text-black transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center mr-3 transition-colors">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <span className="text-sm montserrat">angelinaalriche@gmail.com</span>
                  </a>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-40 lg:z-[60] transition-opacity duration-300 ${
          showMobileMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            showMobileMenu ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setShowMobileMenu(false)}
        ></div>
        
        {/* Menu Content */}
        <div 
          className={`absolute top-0 right-0 h-full w-full sm:w-80 bg-white shadow-2xl transform transition-transform duration-300 ${
            showMobileMenu ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Navigation Links */}
            <div className={`flex-1 overflow-y-auto px-6 pt-40 pb-8 ${isOverlay ? 'lg:pt-28' : 'lg:pt-24'}`}>
              <nav className="space-y-6">
                {menuItems.map((item, index) => (
                  item.isAnchor ? (
                    <a
                      key={index}
                      href={item.href}
                      onClick={(e) => handleAnchorClick(e, item.href)}
                      className="block text-lg font-medium tracking-wide text-gray-800 hover:text-black transition-colors duration-300 montserrat"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      to={item.href}
                      className="block text-lg font-medium tracking-wide text-gray-800 hover:text-black transition-colors duration-300 montserrat"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span>{item.label}</span>
                        {item.href === '/cart' && cartCount > 0 && (
                          <span className="bg-black text-white text-xs w-6 h-6 rounded-full flex items-center justify-center montserrat">
                            {cartCount}
                          </span>
                        )}
                      </span>
                    </Link>
                  )
                ))}
              </nav>

              {/* Account Link / Logout */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                {user ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 montserrat truncate max-w-[60%]">{user.email}</span>
                    <Link
                      to="/account"
                      className="px-3 py-2 text-sm border border-black rounded hover:bg-black hover:text-white transition-colors montserrat"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {language === 'ru' ? 'Профиль' : language === 'es' ? 'Cuenta' : 'Account'}
                    </Link>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    data-discover="true"
                    className="block text-lg font-medium tracking-wide text-gray-800 hover:text-black transition-colors duration-300 montserrat"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span className="inline-flex items-center gap-2">
                      <i className="ri-user-3-line text-xl"></i>
                      {language === 'ru' ? 'Войти в аккаунт' : language === 'es' ? 'Iniciar sesión' : 'Sign In'}
                    </span>
                  </Link>
                )}
              </div>

              {/* Language Selector with Flags */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 montserrat">Язык / Language</p>
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code)
                      }}
                      className={`flex-1 flex flex-col items-center justify-center px-3 py-2.5 rounded-lg text-xs montserrat transition-all duration-200 ${
                        language === lang.code 
                          ? 'bg-amber-50 text-amber-900 font-medium border-2 border-amber-300 shadow-sm' 
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <span className="mb-1.5 inline-flex items-center justify-center w-6 h-6 rounded border border-gray-200/50 shadow-sm overflow-hidden">
                        {React.createElement(lang.flagComponent, { className: "w-full h-full" })}
                      </span>
                      <span className="text-[10px] font-medium">{lang.fullName}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div className="mt-12 pt-8 border-t border-gray-100">
                <h4 className="text-lg font-semibold mb-6 playfair">{footerT.contacts[language]}</h4>
                
                {/* Contact Info (moved WhatsApp to bottom below email) */}

                {/* Social Media */}
                <div className="flex flex-wrap gap-2">
                  {/* Facebook */}
                  <a
                    href="https://www.facebook.com/angelina.alriche.2025"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-facebook-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* Instagram main */}
                  <a
                    href="https://instagram.com/angelinaalriche"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-600 hover:to-orange-400 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-instagram-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* YouTube */}
                  <a
                    href="https://youtube.com/@Richshel"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-red-600 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-youtube-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* Telegram */}
                  <a
                    href="http://t.me/ALRICHE"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-sky-500 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-telegram-line text-xs text-gray-700 group-hover:text-white transition-colors"></i>
                  </a>
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/34662975699"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="w-7 h-7 rounded-full bg-gray-100 hover:bg-green-500 flex items-center justify-center transition-all duration-300 group"
                  >
                    <i className="ri-whatsapp-line text-xs text-green-600 group-hover:text-white transition-colors"></i>
                  </a>
                </div>
                
                {/* Email at the very bottom */}
                <div className="mt-6">
                  <a 
                    href="mailto:angelinaalriche@gmail.com"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center text-gray-700 hover:text-black transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center mr-3 transition-colors">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <span className="text-sm montserrat">angelinaalriche@gmail.com</span>
                  </a>
                </div>

              </div>
            </div>

            {/* Footer Actions */}
            {/* <div className="px-6 py-6 border-t border-gray-100">
              <div className="flex items-center justify-around">
                <button onClick={() => setShowMobileMenu(false)} className="w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
                  <i className="ri-search-line text-xl"></i>
                </button>
                <button onClick={() => setShowMobileMenu(false)} className="w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
                  <i className="ri-user-line text-xl"></i>
                </button>
                <button onClick={() => setShowMobileMenu(false)} className="w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
                  <i className="ri-heart-line text-xl"></i>
                </button>
                <Link to="/cart" onClick={() => setShowMobileMenu(false)} className="relative w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
                  <i className="ri-shopping-bag-line text-xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute top-0 right-0 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center montserrat">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </>
  )
}

export default Header
